import { useCallback, useEffect, useRef } from 'react';
import type { FieldPath, UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import { useBoatSave } from '@/modules/inventory/hooks/useBoatSave';
import { stripEditableMoney } from '@/modules/inventory/lib/moneyFields';
import { useCan } from '@/modules/tenant/hooks/useTenantAuth';
import type { SaveOutcome } from '@/modules/inventory/services/boatSave';
import type { Boat } from '@/modules/inventory/types/boat.types';
import type { WizardFormData } from '@/modules/inventory/components/boat-form/types/wizard.types';
import {
  buildUpdatePayload,
  diffBoat,
  stableStringify,
  type EditableBoat,
  type FieldConflict,
  type LostRow,
  type ScalarValue,
} from '@/modules/inventory/utils/boat-changes';
import { describeLostRow } from '@/modules/inventory/utils/boat-change-labels';
import { writePendingChanges } from '@/modules/inventory/utils/pending-boat-changes';
import { boatToCanvasEditable, pairAddedRows, withDeferredKeysFrom } from '../lib/canvasEditable';
import type { CanvasHydration } from './useCanvasHydration';

/**
 * #1177 (spec §2, §3.1) — the canvas's only save: autosave, Save draft and the
 * Create finalize all send the difference between the form and the last server
 * snapshot, with `version` (utils/boat-changes + useBoatSave).
 *
 * `watch` only triggers a recompute of that difference; a save is scheduled
 * when it is non-empty (decision D40). Hydration updates the snapshot before it
 * writes the form, so its writes never schedule one. Status is held until Create
 * (CANVAS_DEFERRED_KEYS).
 */

export const CANVAS_AUTOSAVE_MS = 5_000;

/** S4-37 (I2) — consecutive background saves that meet two 409s before autosave gives up and says so. */
export const CANVAS_STALE_ROUNDS = 3;

const KEEPS_CHANGING = 'This boat keeps changing elsewhere. Close the canvas and open it again.';

export interface CanvasConflict {
  fresh: Boat;
  merged: EditableBoat;
  conflicts: FieldConflict[];
  lostRows: LostRow[];
}

export interface UseCanvasAutoSaveArgs {
  methods: UseFormReturn<WizardFormData>;
  boatId: string | null;
  enabled: boolean;
  hydration: Pick<CanvasHydration, 'hasHydrated' | 'getSnapshot' | 'readForm' | 'adopt' | 'refresh'>;
  onConflicts: (conflict: CanvasConflict) => void;
}

export interface CanvasAutoSave {
  /** Save now (Save draft, Create). `extra` keys are sent whatever the diff says. Null before the first read. */
  saveNow: (options?: { extra?: Record<string, ScalarValue> }) => Promise<SaveOutcome | null>;
  isSaving: boolean;
}

/** S4-26 — a row the dealer edited that was removed elsewhere is reported, never dropped silently. */
function reportLostRows(rows: LostRow[]): void {
  for (const row of rows) {
    toast.error(`${describeLostRow(row)} was removed elsewhere; your changes to it were not saved.`);
  }
}

/** 422 paths are request paths; the canvas holds the trailer as flat fields. */
function toFormPath(path: string): string {
  const trailer = /^trailers\.0\.(trailer_[a-z_]+)$/.exec(path);
  return trailer ? trailer[1] : path;
}

export function useCanvasAutoSave({
  methods,
  boatId,
  enabled,
  hydration,
  onConflicts,
}: UseCanvasAutoSaveArgs): CanvasAutoSave {
  const boatSave = useBoatSave(boatId, 'canvas');
  // #1408 (D-08) — without finance.cost.view no money scalar or equipment cost is
  // part of what is diffed, so neither a stale form value nor a derived
  // total_cost is ever sent (the server would answer 422).
  const canViewCost = useCan('finance.cost.view');
  const canViewCostRef = useRef(canViewCost);
  canViewCostRef.current = canViewCost;
  /** The snapshot and the form as this viewer may send them. */
  const readStates = useCallback((): { base: EditableBoat; current: EditableBoat } | null => {
    const base = hydrationRef.current.getSnapshot();
    const current = hydrationRef.current.readForm();
    if (!base || !current) return null;
    return {
      base: stripEditableMoney(base, canViewCostRef.current),
      current: stripEditableMoney(current, canViewCostRef.current),
    };
  }, []);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const runningRef = useRef<Promise<SaveOutcome | null> | null>(null);
  /** A payload that failed (conflict, 422, stale, network): not retried until it changes. */
  const heldKeyRef = useRef<string | null>(null);
  /** S4-37 (I2) — consecutive background `stale` outcomes; reset by any `saved`. */
  const staleRoundsRef = useRef(0);
  /** S4-37 (I2) — nothing is scheduled once the canvas has unmounted or autosave is off. */
  const mountedRef = useRef(false);
  const armedRef = useRef(false);
  armedRef.current = enabled && Boolean(boatId) && hydration.hasHydrated;
  const hydrationRef = useRef(hydration);
  hydrationRef.current = hydration;
  const onConflictsRef = useRef(onConflicts);
  onConflictsRef.current = onConflicts;
  const saveRef = useRef(boatSave.save);
  saveRef.current = boatSave.save;

  /**
   * The payload autosave would send now, as a comparable key ('' when nothing changed).
   * S4-37 (I1) — without `version`: a re-read that leaves the dealer's change as it
   * was must not release a held conflict or 422; only a different change does.
   */
  const pendingKey = useCallback((): string | null => {
    const states = readStates();
    if (!states) return null;
    const { base, current } = states;
    const { payload, isEmpty } = buildUpdatePayload(diffBoat(base, withDeferredKeysFrom(current, base)), base.version);
    if (isEmpty) return '';
    const { version: _version, ...change } = payload;
    return stableStringify(change);
  }, [readStates]);

  const finish = useCallback(
    (outcome: SaveOutcome, key: string | null, background: boolean, started: EditableBoat) => {
      switch (outcome.kind) {
        case 'saved':
          heldKeyRef.current = null;
          staleRoundsRef.current = 0;
          // S4-35 — rebase on the state this save started from, never `outcome.sent`:
          // after a silent 409 rebase-and-resend `sent` carries the server's values,
          // and rebasing the form on it would revert them and freeze them as edits.
          hydrationRef.current.adopt(outcome.boat, started);
          reportLostRows(outcome.lostRows);
          return;
        case 'conflicts':
          heldKeyRef.current = key;
          onConflictsRef.current({
            fresh: outcome.fresh,
            merged: outcome.merged,
            conflicts: outcome.conflicts,
            lostRows: outcome.lostRows,
          });
          return;
        case 'invalid':
          heldKeyRef.current = key;
          for (const [path, messages] of Object.entries(outcome.errors)) {
            methods.setError(toFormPath(path) as FieldPath<WizardFormData>, { message: messages[0] });
          }
          toast.error(outcome.message);
          return;
        case 'stale':
          if (background) {
            // Review Minor 3 — a two-phase run bumps the version twice in a row (the
            // merge, then the name refresh), so a background save can meet two 409s.
            // S4-37 (I2) — re-read so the next attempt starts from a fresh snapshot,
            // and try again after the next debounce without a toast, a bounded
            // number of times; then hold the change and say so once.
            staleRoundsRef.current += 1;
            void hydrationRef.current.refresh();
            if (staleRoundsRef.current < CANVAS_STALE_ROUNDS) {
              scheduleRef.current();
              return;
            }
          }
          heldKeyRef.current = key;
          toast.error(KEEPS_CHANGING);
          return;
        case 'nothing_to_save':
          // There was something to send and the rebase consumed it: the snapshot is
          // behind the server. Re-read, or every later keystroke resends the same
          // edit, meets the same 409 and reports the same lost row again.
          // S4-37 (m1) — the re-read's own rebase reports a lost row ("…not kept"),
          // so it is reported here only when there is no re-read.
          if (key) {
            void hydrationRef.current.refresh();
            return;
          }
          // S4-26 — a rebase can consume every change and still have lost a row.
          reportLostRows(outcome.lostRows);
          return;
      }
    },
    [methods],
  );

  const run = useCallback(
    async (extra: Record<string, ScalarValue> | undefined, background: boolean): Promise<SaveOutcome | null> => {
      while (runningRef.current) await runningRef.current.catch(() => null); // one save at a time
      const states = readStates();
      if (!states) return null;
      const { base, current } = states;
      const canViewCostNow = canViewCostRef.current;
      const key = pendingKey();
      const started = withDeferredKeysFrom(current, base);

      const task = (async () => {
        try {
          const outcome = await saveRef.current({
            base,
            current: started,
            extra,
            toEditable: (fresh: Boat) => boatToCanvasEditable(fresh, canViewCostNow),
            afterRebase: pairAddedRows, // S4-39: a typed engine meets the read's engine as one row
          });
          finish(outcome, key, background, started);
          return outcome;
        } finally {
          runningRef.current = null;
        }
      })();
      runningRef.current = task;
      return task;
    },
    [finish, pendingKey, readStates],
  );

  const schedule = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (!mountedRef.current || !armedRef.current) return; // S4-37 (I2)
    const key = pendingKey();
    if (!key || key === heldKeyRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      run(undefined, true).catch((error: unknown) => {
        heldKeyRef.current = key;
        console.warn('[useCanvasAutoSave] autosave failed', error);
      });
    }, CANVAS_AUTOSAVE_MS);
  }, [pendingKey, run]);
  const scheduleRef = useRef(schedule);
  scheduleRef.current = schedule;

  // Declared before the effects that schedule, so their first `schedule()` sees it.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled || !boatId || !hydration.hasHydrated) return;
    const subscription = methods.watch(() => schedule());
    schedule(); // changes made before autosave was armed (the seeded locations, a restored session)
    return () => {
      subscription.unsubscribe();
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [enabled, boatId, hydration.hasHydrated, methods, schedule]);

  const saveNow = useCallback(
    async (options?: { extra?: Record<string, ScalarValue> }) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      return run(options?.extra, false);
    },
    [run],
  );

  // Review I7 — a change still inside the debounce is not lost when the canvas
  // closes: on unmount it is saved (the request outlives the component; its
  // pending-store entry covers a failure), and on a page unload — where no
  // request can be trusted to finish — it is written to the pending store, from
  // which the next open restores it (useCanvasHydration).
  // S4-37 — a held payload is not sent again on close (m3), and a stale outcome of
  // this last save arms nothing: `mountedRef` is false by then (I2).
  const runRef = useRef(run);
  runRef.current = run;
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      const key = pendingKey();
      if (!armedRef.current || !key || key === heldKeyRef.current) return;
      void runRef.current(undefined, true).catch(() => undefined);
    };
  }, [pendingKey]);

  useEffect(() => {
    if (!enabled || !boatId) return;
    const onUnload = () => {
      const states = readStates();
      if (!states || !pendingKey()) return;
      const { base, current } = states;
      writePendingChanges('canvas', boatId, {
        base,
        current: withDeferredKeysFrom(current, base),
        savedAt: new Date().toISOString(),
      });
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [enabled, boatId, pendingKey, readStates]);

  return { saveNow, isSaving: boatSave.isSaving };
}
